/**
 * @file pages/Contact.jsx
 * @description Premium contact page with hero, contact cards, form,
 * Google Maps embed, FAQ accordion, and WhatsApp quick chat.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Phone, Mail, MapPin, Clock, MessageCircle, Send,
  ChevronDown, ChevronRight, Globe, Headphones,
  ArrowRight, CheckCircle2, User, Building2
} from 'lucide-react';
import Header from '../Components/Layouts/Header';
import Footer from '../Components/Layouts/Footer';
import AnimatedButton from '../Components/Ui/AnimatedButton';
import { useSiteSettings } from '../Context/SiteSettingsContext';
import { toast } from 'react-toastify';

// ─── FAQ Data ────────────────────────────────────────────────────
const FAQS = [
  { q: 'How do I book a tour package?', a: 'Browse our tours page, select your preferred package, choose your dates and group size, then proceed to checkout. You can also call us directly for custom bookings.' },
  { q: 'What is your cancellation policy?', a: 'Free cancellation up to 7 days before departure. 50% refund for cancellations 3-7 days prior. No refund for cancellations less than 3 days before departure.' },
  { q: 'Do you offer customized tour packages?', a: 'Yes! We specialize in creating personalized itineraries. Contact us with your preferences, budget, and group size, and our travel experts will craft the perfect trip for you.' },
  { q: 'What payment methods do you accept?', a: 'We accept UPI, credit/debit cards, net banking, and international cards via Razorpay and Stripe. EMI options are also available on select bookings.' },
  { q: 'How can I get a group discount?', a: 'Groups of 4 or more automatically get up to 20% off. For larger groups (10+), contact us directly for special corporate/group rates.' },
  { q: 'Do you provide travel insurance?', a: 'Yes, we offer comprehensive travel insurance as an add-on during booking. It covers trip cancellation, medical emergencies, and baggage loss.' },
];

// ─── FAQ Item ────────────────────────────────────────────────────
const FaqItem = ({ faq, isOpen, onClick }) => (
  <div className="border-b border-gray-100 last:border-0">
    <button onClick={onClick} className="w-full flex items-center justify-between py-5 px-1 text-left group">
      <span className={`text-sm font-semibold transition-colors ${isOpen ? 'text-[#243185]' : 'text-gray-800 group-hover:text-[#243185]'}`}>
        {faq.q}
      </span>
      <motion.span animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
        <ChevronDown size={18} className={isOpen ? 'text-[#243185]' : 'text-gray-400'} />
      </motion.span>
    </button>
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="overflow-hidden"
        >
          <p className="text-sm text-gray-500 leading-relaxed pb-5 px-1">{faq.a}</p>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

const Contact = () => {
  const { settings } = useSiteSettings();
  const co = settings?.companyInfo || {};
  const addr = co.address || {};
  const social = settings?.socialLinks || {};

  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const phone = co.phone || '+91 800 2406 800';
  const email = co.email || 'nikabatravels@gmail.com';
  const whatsapp = co.whatsapp || '918002406800';
  const fullAddress = [addr.street || '34-A, DDA Flats, Mata Sundari Road', addr.city ? `${addr.city} - ${addr.postalCode || '110002'}` : 'New Delhi - 110002'].join(', ');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    // Simulate send — replace with real API when ready
    await new Promise(r => setTimeout(r, 1500));
    setSending(false);
    setSent(true);
    toast.success('Message sent! We\'ll get back to you within 24 hours.');
    setTimeout(() => { setSent(false); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }, 3000);
  };

  const contactCards = [
    { icon: Phone, title: 'Call Us', detail: phone, sub: 'Mon-Sat, 9AM-8PM IST', href: `tel:${phone.replace(/\s/g, '')}`, color: 'blue' },
    { icon: Mail, title: 'Email Us', detail: email, sub: 'We reply within 24 hours', href: `mailto:${email}`, color: 'purple' },
    { icon: MapPin, title: 'Visit Us', detail: fullAddress, sub: 'Walk-ins welcome', href: `https://maps.google.com/?q=${encodeURIComponent(fullAddress)}`, color: 'rose' },
    { icon: MessageCircle, title: 'WhatsApp', detail: 'Chat with us now', sub: 'Instant support', href: `https://wa.me/${whatsapp}`, color: 'green' },
  ];

  const colorMap = {
    blue: { bg: 'bg-blue-50', icon: 'text-blue-600', hover: 'hover:bg-blue-50', border: 'border-blue-100' },
    purple: { bg: 'bg-purple-50', icon: 'text-purple-600', hover: 'hover:bg-purple-50', border: 'border-purple-100' },
    rose: { bg: 'bg-rose-50', icon: 'text-rose-600', hover: 'hover:bg-rose-50', border: 'border-rose-100' },
    green: { bg: 'bg-emerald-50', icon: 'text-emerald-600', hover: 'hover:bg-emerald-50', border: 'border-emerald-100' },
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Contact Us | Nikaba Travels</title>
        <meta name="description" content="Get in touch with Nikaba Travels for tour bookings, custom itineraries, and travel assistance. Call, email, WhatsApp, or visit our Delhi office." />
      </Helmet>
      <Header />

      {/* ════ HERO ════ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a2463] via-[#243185] to-[#4f5fd6]" />
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.15\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 md:pt-36 md:pb-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-white/60 text-sm mb-6">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <ChevronRight size={14} />
              <span className="text-white">Contact</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight">
              Let's Start a <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-purple-300">Conversation</span>
            </h1>
            <p className="text-white/70 text-lg mt-4 max-w-xl">
              Have a question about our tours, need help with a booking, or want a custom itinerary? Our travel experts are here to help.
            </p>
          </motion.div>
        </div>

        {/* Wave divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 80V40C240 80 480 0 720 40C960 80 1200 0 1440 40V80H0Z" fill="#f9fafb" />
          </svg>
        </div>
      </section>

      {/* ════ CONTACT CARDS ════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {contactCards.map((card, i) => {
            const c = colorMap[card.color];
            return (
              <motion.a
                key={card.title}
                href={card.href}
                target={card.title === 'Visit Us' || card.title === 'WhatsApp' ? '_blank' : undefined}
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className={`bg-white rounded-2xl p-6 border ${c.border} ${c.hover} transition-all duration-300 hover:shadow-lg hover:-translate-y-1 group block`}
              >
                <div className={`w-12 h-12 rounded-xl ${c.bg} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <card.icon size={22} className={c.icon} />
                </div>
                <h3 className="text-sm font-bold text-gray-900 mb-1">{card.title}</h3>
                <p className="text-sm font-semibold text-gray-700">{card.detail}</p>
                <p className="text-xs text-gray-400 mt-1">{card.sub}</p>
              </motion.a>
            );
          })}
        </div>
      </section>

      {/* ════ FORM + MAP ════ */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid lg:grid-cols-5 gap-8">
          {/* ── Contact Form ── */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-3 bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-8 md:p-10"
          >
            <div className="mb-8">
              <h2 className="text-2xl md:text-3xl font-black text-gray-900">Send us a Message</h2>
              <p className="text-gray-500 mt-2 text-sm">Fill out the form and our team will get back to you within 24 hours.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid md:grid-cols-2 gap-5">
                {/* Name */}
                <div className="relative">
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Full Name *</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300" />
                    <input
                      type="text" required value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="John Doe"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-800 outline-none focus:border-[#243185] focus:ring-2 focus:ring-[#243185]/10 transition-all placeholder:text-gray-300"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Email *</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300" />
                    <input
                      type="email" required value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="john@example.com"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-800 outline-none focus:border-[#243185] focus:ring-2 focus:ring-[#243185]/10 transition-all placeholder:text-gray-300"
                    />
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-5">
                {/* Phone */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Phone</label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300" />
                    <input
                      type="tel" value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+91 98765 43210"
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-800 outline-none focus:border-[#243185] focus:ring-2 focus:ring-[#243185]/10 transition-all placeholder:text-gray-300"
                    />
                  </div>
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Subject</label>
                  <div className="relative">
                    <Building2 size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300" />
                    <select
                      value={form.subject}
                      onChange={(e) => setForm({ ...form, subject: e.target.value })}
                      className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-800 outline-none focus:border-[#243185] focus:ring-2 focus:ring-[#243185]/10 transition-all appearance-none bg-white"
                    >
                      <option value="">Select a topic</option>
                      <option value="booking">Tour Booking</option>
                      <option value="custom">Custom Itinerary</option>
                      <option value="flights">Flight Inquiry</option>
                      <option value="support">Support / Issue</option>
                      <option value="corporate">Corporate / Group</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div>
                <label className="block text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">Message *</label>
                <textarea
                  required rows={5} value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us how we can help you..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm font-medium text-gray-800 outline-none focus:border-[#243185] focus:ring-2 focus:ring-[#243185]/10 transition-all placeholder:text-gray-300 resize-none"
                />
              </div>

              <AnimatedButton
                type="submit"
                loading={sending}
                success={sent}
                disabled={sending}
                variant="gradient"
                size="lg"
                fullWidth
                icon={sent ? CheckCircle2 : Send}
              >
                {sent ? 'Message Sent!' : 'Send Message'}
              </AnimatedButton>
            </form>
          </motion.div>

          {/* ── Map + Office Hours ── */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Google Maps */}
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 overflow-hidden">
              <iframe
                title="Nikaba Travels Office"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.0!2d77.234!3d28.6339!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x390cfd0!2sDDA+Flats+Mata+Sundari+Road!5e0!3m2!1sen!2sin!4v1700000000000"
                className="w-full h-56 border-0"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="p-5">
                <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                  <MapPin size={15} className="text-[#243185]" />
                  Our Office
                </h3>
                <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{fullAddress}</p>
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(fullAddress)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#243185] mt-3 hover:underline"
                >
                  Get Directions <ArrowRight size={12} />
                </a>
              </div>
            </div>

            {/* Office Hours */}
            <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-6">
              <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2 mb-4">
                <Clock size={15} className="text-[#243185]" />
                Office Hours
              </h3>
              <div className="space-y-3">
                {[
                  { day: 'Monday - Friday', time: '9:00 AM - 8:00 PM', active: true },
                  { day: 'Saturday', time: '10:00 AM - 6:00 PM', active: true },
                  { day: 'Sunday', time: 'Closed', active: false },
                ].map(h => (
                  <div key={h.day} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{h.day}</span>
                    <span className={`text-sm font-semibold ${h.active ? 'text-gray-900' : 'text-gray-400'}`}>{h.time}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-3 bg-emerald-50 rounded-xl flex items-center gap-3">
                <Headphones size={18} className="text-emerald-600" />
                <div>
                  <p className="text-xs font-bold text-emerald-800">24/7 Phone Support</p>
                  <p className="text-[10px] text-emerald-600">For urgent travel assistance</p>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-gradient-to-br from-[#243185] to-[#4f5fd6] rounded-2xl p-6 text-white">
              <h3 className="font-bold text-sm mb-4 flex items-center gap-2">
                <Globe size={15} /> Quick Links
              </h3>
              <div className="space-y-2">
                {[
                  { label: 'Browse Tours', to: '/tours' },
                  { label: 'Search Flights', to: '/search?tab=flights' },
                  { label: 'Read Our Blog', to: '/blogs' },
                  { label: 'About Us', to: '/about' },
                ].map(link => (
                  <Link key={link.to} to={link.to}
                    className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-white/10 transition-colors group"
                  >
                    <span className="text-sm font-medium text-white/90">{link.label}</span>
                    <ArrowRight size={14} className="text-white/40 group-hover:text-white group-hover:translate-x-1 transition-all" />
                  </Link>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ════ FAQ ════ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-black text-gray-900">Frequently Asked Questions</h2>
            <p className="text-gray-500 mt-2 text-sm">Quick answers to common queries</p>
          </div>
          <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/50 p-6 md:p-8">
            {FAQS.map((faq, i) => (
              <FaqItem key={i} faq={faq} isOpen={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? null : i)} />
            ))}
          </div>
        </motion.div>
      </section>

      {/* ════ Floating WhatsApp ════ */}
      <a
        href={`https://wa.me/${whatsapp}?text=Hi! I need help with a travel booking.`}
        target="_blank" rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full shadow-lg shadow-emerald-500/30 flex items-center justify-center hover:scale-110 transition-all"
        title="Chat on WhatsApp"
      >
        <MessageCircle size={24} />
      </a>

      <Footer />
    </div>
  );
};

export default Contact;
